import React, {
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  CSSProperties,
} from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useEffectOnce, useWindowSize } from 'react-use';
import { LngLatBoundsLike } from 'mapbox-gl';

import config from '../../../common/utils/constants/config';
import { useMobile } from '../../../common/components/responsive/Responsive';
import { selectHeight as selectWizardHeight } from '../../../common/store/wizard';
import { selectStyle } from '../../store';
import { ENV } from '../../../common/utils/constants/constants';
import StyleSwitchControl from '../controls/style-switch-control/StyleSwitchControl';
import GeolocateControl from '../controls/geolocation-control/GeolocationControl';
import NavigationControl from '../controls/navigation-control/NavigationControl';
import ReduxControl from '../controls/redux-control/ReduxControl';
import LocationSyncControl from '../controls/location-sync-control/LocationSyncControl';
import InitialLoadGeolocationControl from '../controls/initial-geolocation-control/InitialLoadGeolocationControl';
import { MapContextProvider } from '../../contexts/context';
import { LayerEventHandler } from '../../utils/helpers/LayerEventHandler';
import MapController from '../map-controller/MapController';
import { WaterbodiesTransparentLayer } from '../layers/waterbodies-transparent-layer/WaterbodiesTransparentLayer';
import { MapWaterPolygonProvider } from '../../contexts/mapWaterPolygonContext';
// import DebugControl from '../controls/debug-control/DebugControl';
// import DebugWaterbodiesPolygonControl from '../controls/debug-waterbodies-control/DebugWaterbodiesPolygonControl';

// https://docs.mapbox.com/mapbox-gl-js/api/#excluding-gl-js-explicitly-from-transpilation
// eslint-disable-next-line
import mapboxgl from '!mapbox-gl';

import './Map.scss';

const WHOLE_WORLD_VIEW_OPTIONS: { bounds: LngLatBoundsLike } = {
  bounds: [
    [-170, -57],
    [192, 80],
  ],
};

const NAVIGATION_CONTROLS_MOBILE_MIN_TOP_SPACE = 280;
const NAVIGATION_CONTROLS_MOBILE_HEIGHT = 200;
const NAVIGATION_CONTROLS_MOBILE_MARGIN = 12;

// 3-4 value is default for mapbox-gl-js
const TOUCH_MOVE_TOLERANCE = 4;

const Map = ({ className }: { className?: string }) => {
  const [glMap, setGlMap] = useState<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const currentMapStyle = useSelector(selectStyle);

  useEffectOnce(() => {
    const container = mapContainerRef.current;

    // container is always initialized at this point
    if (!container) {
      return;
    }

    const map = new mapboxgl.Map({
      container,
      accessToken: config.MAP_ACCESS_TOKEN as string | undefined,
      style: currentMapStyle.url,
      attributionControl: true,
      dragRotate: false,
      maxPitch: 0,
      ...WHOLE_WORLD_VIEW_OPTIONS,
    });

    // disable map rotation using right click + drag
    map.dragRotate.disable();

    // disable map rotation using touch rotation gesture
    map.touchZoomRotate.disableRotation();

    // inject custom layer event handler with layer layout support for events
    map.layerEventHandler = new LayerEventHandler(map);
    setGlMap(map);

    if ([ENV.TEST, ENV.DEV, ENV.LOCAL].includes(config.ENVIRONMENT as string)) {
      window._mapboxglMap = map;
    }

    return () => map.remove();
  });

  useEffect(() => {
    if (glMap) {
      const canvas = glMap.getCanvas();
      const attributionLinksContainers = glMap
        .getContainer()
        .querySelectorAll('.mapboxgl-ctrl');

      // preventing default behavior is required to prevent touch selection on iOS with magnifying glass
      // although click and other events dispatch is needed to keep behavior consistent between touch and non touch devices
      const handleTouchStart = (event) => {
        event.preventDefault();

        if (!event.touches.length) {
          return;
        }

        const touch = event.touches[0];

        // eslint-disable-next-line prefer-const
        let handleTouchMove;
        // eslint-disable-next-line prefer-const
        let handleLongPress;

        const handleTouchEnd = () => {
          canvas.removeEventListener('touchend', handleTouchEnd);
          canvas.removeEventListener('touchmove', handleTouchMove);
          canvas.removeEventListener('long-press', handleLongPress);

          const eventOptions = {
            screenX: touch.screenX,
            screenY: touch.screenY,
            clientX: touch.clientX,
            clientY: touch.clientY,
            x: touch.clientX,
            y: touch.clientY,
            pageX: touch.pageX,
            pageY: touch.pageY,
            bubbles: true,
            cancelable: true,
            view: window,
          };

          canvas.dispatchEvent(new MouseEvent('mousedown', eventOptions));
          canvas.dispatchEvent(new MouseEvent('mousemove', eventOptions));
          canvas.dispatchEvent(new MouseEvent('mouseup', eventOptions));
          canvas.dispatchEvent(new MouseEvent('click', eventOptions));
        };

        handleTouchMove = (touchMoveEvent) => {
          if (touchMoveEvent.touches.length) {
            const moveTouch = touchMoveEvent.touches[0];
            const distance = Math.sqrt(
              (moveTouch.clientX - touch.clientX) ** 2 +
                (moveTouch.clientY - touch.clientY) ** 2,
            );

            if (distance <= TOUCH_MOVE_TOLERANCE) {
              return;
            }
          }

          canvas.removeEventListener('touchend', handleTouchEnd);
          canvas.removeEventListener('touchmove', handleTouchMove);
          canvas.removeEventListener('long-press', handleLongPress);
        };

        handleLongPress = () => {
          canvas.removeEventListener('touchend', handleTouchEnd);
          canvas.removeEventListener('touchmove', handleTouchMove);
          canvas.removeEventListener('long-press', handleLongPress);
        };

        canvas.addEventListener('long-press', handleLongPress);
        canvas.addEventListener('touchend', handleTouchEnd);
        canvas.addEventListener('touchmove', handleTouchMove);
      };
      // Track attribution control links click (e. g. mapbox logo and (c) Mapbox (c) OpenStreetMap in the footer)
      const handleAttributionLinkPointerdown = (e) => {
        if (e.target.tagName === 'A') {
          analytics.track('Transition');
        }
      };

      canvas.addEventListener('touchstart', handleTouchStart);

      attributionLinksContainers.forEach((c) =>
        c.addEventListener('pointerdown', handleAttributionLinkPointerdown),
      );

      return () => {
        canvas.removeEventListener('touchstart', handleTouchStart);

        attributionLinksContainers.forEach((c) =>
          c.removeEventListener(
            'pointerdown',
            handleAttributionLinkPointerdown,
          ),
        );
      };
    }
  }, [glMap]);

  const isMobile = useMobile();
  const wizardHeight = useSelector(selectWizardHeight);
  const { height: windowHeight } = useWindowSize();

  useLayoutEffect(() => {
    if (glMap) {
      glMap.resize();
    }
  }, [glMap, wizardHeight, windowHeight]);

  let mobileControlsStyle: CSSProperties | undefined = undefined;
  if (isMobile) {
    if (
      wizardHeight !== null &&
      windowHeight - wizardHeight < NAVIGATION_CONTROLS_MOBILE_MIN_TOP_SPACE
    ) {
      mobileControlsStyle = {
        bottom: 'initial',
        top:
          NAVIGATION_CONTROLS_MOBILE_MIN_TOP_SPACE -
          NAVIGATION_CONTROLS_MOBILE_HEIGHT -
          NAVIGATION_CONTROLS_MOBILE_MARGIN,
      };
    } else if (
      wizardHeight !== null &&
      wizardHeight >
        windowHeight / 2 -
          NAVIGATION_CONTROLS_MOBILE_HEIGHT / 2 -
          NAVIGATION_CONTROLS_MOBILE_MARGIN
    ) {
      mobileControlsStyle = {
        bottom: NAVIGATION_CONTROLS_MOBILE_MARGIN + wizardHeight,
      };
    } else {
      mobileControlsStyle = {
        top: windowHeight / 2 - NAVIGATION_CONTROLS_MOBILE_HEIGHT / 2,
      };
    }
  }

  return (
    <div className={classnames('map', className)}>
      <div ref={mapContainerRef} className="map__container" />
      <MapContextProvider value={glMap}>
        <WaterbodiesTransparentLayer />
        <MapWaterPolygonProvider>
          <div className="map__controls" style={mobileControlsStyle}>
            <GeolocateControl showAccuracyCircle={false} />
            <NavigationControl className="map__navigation-control" />
            <StyleSwitchControl className="map__style-switch-control" />
          </div>
          <ReduxControl />
          <InitialLoadGeolocationControl />
          <LocationSyncControl precision={4} />
          <MapController />
          {/* <DebugControl showPadding={true} /> */}
          {/* <DebugWaterbodiesPolygonControl /> */}
        </MapWaterPolygonProvider>
      </MapContextProvider>
    </div>
  );
};

export default Map;
